/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { Button, Dialog, Tag, Typography2 } from '@coursera/cds-core';

import SeoSkillsLinkTag from 'bundles/seo/components/SeoSkillsLinkTag';
import { useSkillsAndToolsDialog } from 'bundles/unified-description-page-common/contexts/SkillsAndToolsDialogContext';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';

import _t from 'i18n!nls/xdp';

const styles = {
  container: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  section: css`
    margin-bottom: var(--cds-spacing-300);

    &:last-of-type {
      margin-bottom: 0;
    }
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  scrollableContent: css`
    max-height: 400px;
    overflow-y: auto;
  `,
};

type Props = {
  open: boolean;
  onClose: () => void;
  skillTags?: ConsumerSpecializationPageFieldsFragment['skillTags'];
};

const SkillsAndToolsDialog = ({ open, onClose, skillTags }: Props) => {
  const { isSkillsLinkingEnabled } = useSkillsAndToolsDialog();
  const { skills, tools } = useMemo(() => {
    if (!skillTags?.length) {
      return { skills: [], tools: [] };
    }

    const skillsList = skillTags.filter((skillTag) => !skillTag.isToolSoftware);
    const toolsList = skillTags.filter((skillTag) => skillTag.isToolSoftware);

    return { skills: skillsList, tools: toolsList };
  }, [skillTags]);

  const renderTag = (key: string, skill: string) => {
    if (isSkillsLinkingEnabled) {
      return <SeoSkillsLinkTag key={key} skill={skill} />;
    } else {
      return <Tag key={key}>{skill}</Tag>;
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.HeadingGroup>{_t("Skills and tools you'll learn")}</Dialog.HeadingGroup>
      <Dialog.Content>
        <div css={styles.scrollableContent}>
          {skills.length > 0 && (
            <div css={styles.section}>
              <Typography2 component="h3" variant="subtitleMedium" css={styles.heading}>
                {_t("Skills you'll gain")}
              </Typography2>
              <div css={styles.container}>{skills.map((skill) => renderTag(skill.id, skill.name))}</div>
            </div>
          )}
          {tools.length > 0 && (
            <div css={styles.section}>
              <Typography2 component="h3" variant="subtitleMedium" css={styles.heading}>
                {_t("Tools you'll learn")}
              </Typography2>
              <div css={styles.container}>{tools.map((tool) => renderTag(tool.id, tool.name))}</div>
            </div>
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="primary" size="medium" onClick={onClose}>
          {_t('OK')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default SkillsAndToolsDialog;
